export type AuthAction = "login" | "signup";

type BackendAuthErrorDetail =
  | "email_and_password_required"
  | "invalid_email"
  | "password_too_short"
  | "email_already_exists"
  | "account_not_found"
  | "incorrect_password"
  // legacy/back-compat
  | "invalid_credentials"
  | string;

export class AuthApiError extends Error {
  name = "AuthApiError" as const;
  action: AuthAction;
  status: number;
  detail?: BackendAuthErrorDetail;
  payload?: unknown;

  constructor(args: { action: AuthAction; status: number; detail?: BackendAuthErrorDetail; message: string; payload?: unknown }) {
    super(args.message);
    this.action = args.action;
    this.status = args.status;
    this.detail = args.detail;
    this.payload = args.payload;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function getAuthErrorMessage(action: AuthAction, err: unknown): string {
  // Network / fetch failures often come through as TypeError without a status code.
  if (err instanceof TypeError) {
    return "Network error. Please check your connection and try again.";
  }

  // Our structured backend error
  if (err instanceof AuthApiError) {
    const detail = err.detail;
    if (action === "login") {
      if (detail === "account_not_found") return "No account found for that email. Check the email or sign up.";
      if (detail === "incorrect_password") return "Incorrect password. Try again or reset your password.";
      if (detail === "email_and_password_required") return "Please enter your email and password.";
      if (detail === "invalid_credentials") return "Incorrect email or password.";
      if (err.status === 429) return "Too many attempts. Please wait a moment and try again.";
      return "Couldn’t sign you in. Please try again.";
    }

    // signup
    if (detail === "email_already_exists") return "An account with this email already exists. Try logging in instead.";
    if (detail === "invalid_email") return "Please enter a valid email address.";
    if (detail === "password_too_short") return "Password must be at least 8 characters long.";
    if (detail === "email_and_password_required") return "Please enter your email and password.";
    if (err.status === 429) return "Too many attempts. Please wait a moment and try again.";
    return "Couldn’t create your account. Please try again.";
  }

  // Generic Error fallback (keeps current behavior if something else throws)
  if (err instanceof Error) {
    const msg = err.message?.trim();
    if (msg) return msg;
  }

  // Last resort
  if (isRecord(err)) {
    const maybeMessage = err.message;
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage.trim();
  }

  return action === "login" ? "Login failed. Please try again." : "Signup failed. Please try again.";
}


